/**
 * Where in a passage a remark points.
 *
 * A comment names a block, which never moves — but a remark is usually about a
 * few words inside it, and words move every time somebody edits the paragraph
 * around them. So the words themselves are what is stored, and the position is
 * only a hint: an offset alone silently slides onto whatever now occupies it,
 * which is a comment about a phrase nobody wrote.
 *
 * The three parts are the W3C Web Annotation selectors, and each answers a
 * question the others cannot:
 *
 * - **the quote** — what was selected. The authority.
 * - **prefix and suffix** — the words either side, which is the only thing that
 *   tells one occurrence of a common word from the next.
 * - **the offset** — where it was, so the ordinary case costs a comparison
 *   rather than a search.
 *
 * When the quote is gone the anchor is ORPHANED, and that is a state to show
 * rather than a failure to hide: the remark falls back to the passage and says
 * the words it was about have changed. It is the rule pointing already keeps —
 * a word is only ever marked where its letters are unchanged.
 */

/**
 * How much of the surrounding text is kept either side.
 *
 * Enough to separate two occurrences of a phrase in one paragraph, which is all
 * this has to do — the search is already narrowed to a single block, so it is
 * never asked to tell one page from another.
 */
export const ANCHOR_CONTEXT = 32

export type TextAnchor = {
  /** The words the remark is about. Empty means the whole passage. */
  quote: string
  prefix: string | null
  suffix: string | null
  /** Where it was found last time. A hint, never the answer. */
  start: number | null
}

/** Where an anchor lands now, or `null` where its words are gone. */
export type Anchored = { start: number; end: number }

/** The anchor for a stretch of a passage, ready to store. */
export function describeAnchor(
  text: string,
  start: number,
  end: number,
): TextAnchor {
  return {
    quote: text.slice(start, end),
    prefix: text.slice(Math.max(0, start - ANCHOR_CONTEXT), start) || null,
    suffix: text.slice(end, end + ANCHOR_CONTEXT) || null,
    start,
  }
}

/**
 * Where the anchor's words are in this text now.
 *
 * The offset is tried first and believed only if the words are still under it.
 * Otherwise every occurrence is considered and the context decides, because a
 * paragraph that says "the same" three times gives three exact matches and only
 * what surrounds them can say which one somebody meant.
 */
export function resolveAnchor(
  text: string,
  anchor: TextAnchor,
): Anchored | null {
  const { quote } = anchor
  if (!quote) return null

  const at = anchor.start
  if (at !== null && text.slice(at, at + quote.length) === quote) {
    return { start: at, end: at + quote.length }
  }

  const hits: number[] = []
  for (let i = text.indexOf(quote); i !== -1; i = text.indexOf(quote, i + 1)) {
    hits.push(i)
  }
  if (hits.length === 0) return null
  if (hits.length === 1) {
    return { start: hits[0] as number, end: (hits[0] as number) + quote.length }
  }

  let best = hits[0] as number
  let bestScore = Number.NEGATIVE_INFINITY
  for (const hit of hits) {
    const score =
      endsWithScore(text.slice(0, hit), anchor.prefix) +
      startsWithScore(text.slice(hit + quote.length), anchor.suffix) -
      // A tie between two equally-surrounded occurrences goes to the one
      // nearest where it was, which is the offset earning its keep again.
      (at === null ? 0 : Math.abs(hit - at) / (text.length + 1))
    if (score > bestScore) {
      bestScore = score
      best = hit
    }
  }
  return { start: best, end: best + quote.length }
}

/** How many characters of `want` end the text before a match. */
function endsWithScore(before: string, want: string | null): number {
  if (!want) return 0
  let n = 0
  while (
    n < want.length &&
    n < before.length &&
    before[before.length - 1 - n] === want[want.length - 1 - n]
  ) {
    n++
  }
  return n
}

/** How many characters of `want` open the text after a match. */
function startsWithScore(after: string, want: string | null): number {
  if (!want) return 0
  let n = 0
  while (n < want.length && n < after.length && after[n] === want[n]) n++
  return n
}

/**
 * The same text with every run of whitespace as one space.
 *
 * What a reader selects on screen and what a block holds are the same words
 * separated differently — a line break between two paragraphs of a verse, a
 * space the renderer puts between turns. Comparing the words means comparing
 * them without their spacing.
 */
export const collapseSpace = (text: string): string =>
  text.replace(/\s+/g, ' ').trim()
